import type { ConditionalRule, DashboardTheme, Locale, WidgetAppearance } from './types';
import { dashboardPalette, DEFAULT_DASHBOARD_THEME } from './theme';

let activeDashboardTheme: DashboardTheme = DEFAULT_DASHBOARD_THEME;
let activeLocale: Locale = 'ru';
let activeResolvedMode: 'light' | 'dark' = 'light';

export function setVisualContext(theme?: DashboardTheme, locale: Locale = 'ru', resolvedMode: 'light' | 'dark' = 'light'): void {
  activeDashboardTheme = theme ?? DEFAULT_DASHBOARD_THEME;
  activeLocale = locale;
  activeResolvedMode = resolvedMode;
}

export function cssToken(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

const PALETTES: Record<Exclude<WidgetAppearance['palette'], 'dashboard'>, string[]> = {
  blue: ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e3a8a'],
  green: ['#ecfdf5', '#d1fae5', '#a7f3d0', '#6ee7b7', '#34d399', '#10b981', '#059669', '#047857', '#064e3b'],
  teal: ['#f0fdfa', '#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#14b8a6', '#0d9488', '#0f766e', '#134e4a'],
  orange: ['#fff7ed', '#ffedd5', '#fed7aa', '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c', '#7c2d12'],
  red: ['#fff1f2', '#ffe4e6', '#fecdd3', '#fda4af', '#fb7185', '#f43f5e', '#e11d48', '#be123c', '#881337'],
  purple: ['#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7e22ce', '#581c87'],
  viridis: ['#440154', '#482878', '#3e4989', '#31688e', '#26828e', '#1f9e89', '#35b779', '#6ece58', '#fde725'],
  cividis: ['#00204c', '#173f5f', '#315c6e', '#4c7775', '#699176', '#89aa70', '#acc164', '#d2d74c', '#fee838'],
  grayscale: ['#f8fafc', '#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b', '#0f172a'],
  diverging: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#f7f7f7', '#fdae61', '#f46d43', '#d73027', '#a50026'],
};

export function paletteColors(
  appearance: WidgetAppearance,
  requested?: number,
  theme?: DashboardTheme,
): string[] {
  const count = Math.max(2, Math.min(9, requested ?? appearance.classCount));
  if (appearance.palette === 'dashboard') {
    const colors = dashboardPalette(theme ?? activeDashboardTheme, count, activeResolvedMode);
    return appearance.reversePalette ? colors.reverse() : colors;
  }
  const source = [...PALETTES[appearance.palette]];
  const colors: string[] = [];
  if (count === source.length) {
    colors.push(...source);
  } else {
    for (let i = 0; i < count; i += 1) {
      const index = Math.round((i / Math.max(1, count - 1)) * (source.length - 1));
      colors.push(source[index] ?? source[source.length - 1] ?? '#2563eb');
    }
  }
  return appearance.reversePalette ? colors.reverse() : colors;
}

export function conditionMatches(value: number | null | undefined, condition: ConditionalRule): boolean {
  if (value == null || condition.operator === 'none' || condition.value == null) return false;
  switch (condition.operator) {
    case 'gt':
      return value > condition.value;
    case 'gte':
      return value >= condition.value;
    case 'lt':
      return value < condition.value;
    case 'lte':
      return value <= condition.value;
    default:
      return false;
  }
}

export function formatNumber(value: number | null | undefined, decimals = 1, locale: Locale = activeLocale): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.max(0, Math.min(6, decimals)),
  }).format(value);
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    };
    return entities[character] ?? character;
  });
}
